import { createContext, useContext, useMemo, type ReactNode } from 'react'

import type { ChatCalculatorResult } from '@/routes/api/chat/-utils/status-interceptor'

export interface CalculatorInitialDataContextValue {
  calculatorId: string | null
  result: ChatCalculatorResult | null
}

const CalculatorInitialDataContext =
  createContext<CalculatorInitialDataContextValue>({
    calculatorId: null,
    result: null,
  })

export function CalculatorInitialDataProvider({
  calculatorId,
  result,
  children,
}: {
  calculatorId: string | null
  result: ChatCalculatorResult | null | undefined
  children: ReactNode
}) {
  const value = useMemo(
    () => ({ calculatorId, result: result ?? null }),
    [calculatorId, result],
  )

  return (
    <CalculatorInitialDataContext.Provider value={value}>
      {children}
    </CalculatorInitialDataContext.Provider>
  )
}

export function useCalculatorInitialData<T = Record<string, unknown>>(
  expectedCalculatorId?: string,
): {
  result: ChatCalculatorResult | null
  params: Record<string, unknown>
  rawParams: T | null
} {
  const context = useContext(CalculatorInitialDataContext)
  if (!context?.result) {
    return { result: null, params: {}, rawParams: null }
  }

  if (
    expectedCalculatorId &&
    context.calculatorId !== expectedCalculatorId &&
    context.result.calculator_id !== expectedCalculatorId
  ) {
    return { result: null, params: {}, rawParams: null }
  }

  const rawParams = (context.result.raw_params ?? null) as T | null
  const params = context.result.params ?? {}

  return {
    result: context.result,
    params,
    rawParams,
  }
}
